import {AxiosInstance, AxiosResponse} from "axios";
import {
  ProductsWithIdSchema,
  ServicesConfigurationsWithIdSchema,
  ServiceSurveyResponsesWithIdSchema,
  ServiceSurveysWithIdSchema,
  ServicesWithIdSchema,
  SystemsWithIdSchema,
} from "../../types";
import prepareConfigurationStep from "@utils/prepareConfigurationStep";
import _ from "lodash";
import convertSurveyResponses from "@utils/convertSurveyResponses";
import filterActiveSteps from "@utils/filterActiveSteps";
import evaluateOutputCalculation from "@utils/evaluateOutputCalculation";
import {ExtendedConfiguration, ServiceProcessedStep} from "../../services/types";
import {SurveyOutputsType, SurveyOutputValueType} from "../types";
import evaluateJsonOutputValue from "@utils/evaluateJsonOutputValue";
import {ConnectionResultResponse} from "../../services/connettori/types";
import evaluateQuestionOutputValue from "@utils/evaluateQuestionOutputValue";


const retrieveSurveyEntities = async <T>(
  api: AxiosInstance,
  steps: ServiceProcessedStep[],
  baseEntities: {[pid: number]: T},
  target: 'products' | 'systems',
) => {
  const missingIds = steps
    .reduce(
      (acc, s) => _.uniq([...acc, ...s[target]]),
      [] as number[],
    )
    .filter((id) => !baseEntities[id]);
  let result = baseEntities;
  if (missingIds.length) {
    const missing: T[] = await api.get(
      `/${target}`,
      {params: {ids: missingIds.join(",")}},
    );
    result = {...baseEntities, ..._.keyBy(missing, 'id')};
  }
  return result || [];
}

const retrieveSurveyProducts = (
  api: AxiosInstance,
  steps: ServiceProcessedStep[],
  products: {[pid: number]: ProductsWithIdSchema},
) => retrieveSurveyEntities<ProductsWithIdSchema>(api, steps, products, 'products');

const retrieveSurveySystems = (
  api: AxiosInstance,
  steps: ServiceProcessedStep[],
  systems: {[pid: number]: SystemsWithIdSchema},
) => retrieveSurveyEntities<SystemsWithIdSchema>(api, steps, systems, 'systems');

const isBlockHidden = (blockExcludedRoles: string[] | undefined, role: string) =>
  !!blockExcludedRoles?.includes(`role_type_${role}`);

const getOutputValue = (
  output: ExtendedConfiguration,
  context: any,
): SurveyOutputValueType | undefined  => {
  switch (output.output_type) {
    case "calculation":
      return evaluateOutputCalculation(output, context);
    case "question":
      return evaluateQuestionOutputValue(output, context);
    case "json_output_value":
      return evaluateJsonOutputValue(output, context) ?? null;
    // @ts-ignore
    default:
      console.warn(`Output type ${output.output_type} not handled, returing null value (id=${output.id})`);
    case "separator":
    case "link":
    case "text_image":
      return null;
  }
};

const extractStepOutputs = (
  step: ServiceProcessedStep,
  role: string,
  context: any,
  skipRoleChecks: boolean = false,
): SurveyOutputsType =>
  step.output
    .filter((output) =>
      skipRoleChecks
      || !isBlockHidden(output.hidden_question, role)
    )
    .reduce(
      (acc, output) => {
        // --- CASE A: simple output
        if (output.type !== "recursive_question") {
          const value = getOutputValue(output, context);
          if (value === undefined)
            return acc;
          return {...acc, [output.id]: value};
        }

        // --- CASE B: recursive question
        const recursiveQuestionResponse = context.responses?.[output.id];
        const recursiveQuestionRows: {}[] = recursiveQuestionResponse?.recursive_question_value || [];

        const outputRows = recursiveQuestionRows
          .map((row: any) => {
            const rowContext = {
              ...context,
              responses: {
                ...context.responses,
                ...row,
              },
            };
            const outputValues = output.blocks
              ?.filter((b) =>
                skipRoleChecks
                || !isBlockHidden(b.hidden_question, role)
              )
              ?.reduce(
                (acc, b) => {
                  const value = getOutputValue(b, rowContext);
                  if (value === undefined)
                    return acc;
                  return {...acc, [b.id]: value};
                },
                {} as {[key: number]: SurveyOutputValueType},
              ) ?? {};
            return outputValues as SurveyOutputValueType[];
          });
        return {...acc, [output.id]: outputRows};
      },
      {} as SurveyOutputsType,
    );

export const getSurveyOutputs = async (
  api: AxiosInstance,
  survey: ServiceSurveysWithIdSchema,
  services: ServicesWithIdSchema[],
  products: {[pid: number]: ProductsWithIdSchema},
  systems: {[sid: number]: SystemsWithIdSchema},
  role: string,
  getServiceConfiguration: (surveyId: number, versionNumber: number) => Promise<AxiosResponse>,
  skipRoleChecks: boolean = false,
): Promise<SurveyOutputsType> => {

  // 1. Retrieve survey data
  const service = services
    .find(({id}) => id === survey.service_id);
  if (!service)
    throw Error(`Service ${survey.service_id} not found`);
  const [
    responses,
    configuration,
    connectorsResult,
  ] = await Promise.all([
    // --- survey responses promise
    api.get(
      "/service_survey_responses/all",
      {params: {service_survey_id: survey.id}},
    ) as Promise<ServiceSurveyResponsesWithIdSchema[]>,
    // --- service configurations promise
    getServiceConfiguration(
      service.id, survey.service_version,
    ) as unknown as Promise<ServicesConfigurationsWithIdSchema[]>,
    // --- kcc results promise
    // TODO
    // --- connectors results promise
    api.get(`/connectors/${survey.id}`) as Promise<ConnectionResultResponse>,
  ]);

  // 2. Prepare context
  const steps: ServiceProcessedStep[] = prepareConfigurationStep(configuration, undefined, service.id);
  const [
    surveyProducts,
    surveySystems,
  ] = await Promise.all([
    retrieveSurveyProducts(api, steps, products),
    retrieveSurveySystems(api, steps, systems),
  ]);
  const contextWithOutResponses = {
    data: steps,
    configuration,
    products: surveyProducts,
    systems: surveySystems,
    outputs: steps.reduce((acc, step) => [
      ...acc,
      ...step.output,
    ], [] as ServicesConfigurationsWithIdSchema[]),
    responses: {},
    connectionResults: connectorsResult,
  };
  const convertedResponses = convertSurveyResponses(responses, contextWithOutResponses);
  const context = {...contextWithOutResponses, responses: convertedResponses};

  // 3. Evaluate steps outputs (visibility, calculations, ...)
  return filterActiveSteps(context)
    .filter((step: ServiceProcessedStep) =>
      skipRoleChecks
      || !isBlockHidden(step.hidden_question, role)
    )
    .reduce(
      (acc: any, step: ServiceProcessedStep) => ({
        ...acc,
        ...extractStepOutputs(step, role, context, skipRoleChecks)
      }),
      {} as SurveyOutputsType,
    );
};